import React, {
  useState,
  useEffect,
  KeyboardEvent,
  useRef,
  ChangeEvent,
  forwardRef,
  useImperativeHandle,
} from 'react';
import ReactMarkdown from 'react-markdown';

import { usePathname, useRouter } from 'next/navigation';

import CloseIcon from '@mui/icons-material/Close';
import CommentIcon from '@mui/icons-material/Comment';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import remarkGfm from 'remark-gfm';

import { useUserInfo } from '@/context/UserInfoContext';
import { useVisibilityError } from '@/hooks/useVisibilityError';
import { DOC_VISIBILITY, DocVisibility } from '@/lib/api';
import {
  DisplayUserFollow,
  NewDocRequest,
  ProductPostType,
  PublicDocId,
  asPublicDocId,
} from '@/lib/api';
import { API_URL } from '@/lib/consts';
import { getSubmitShortcutLabel, isApplePlatform } from '@/lib/utils';

import { InviteForm } from './InviteForm';
import { Modal } from './Modal';
import { VisibilityIcon, VisibilitySelector } from './Visibility';

const FEEDBACK_USERNAME = 'feedback';
const INVITE_SUGGESTION_INDEX = -1;

type SuggestionSelectionIndex = number | null;

const getFollowedUsersSignature = (followedUsers: DisplayUserFollow[] | undefined): string =>
  JSON.stringify((followedUsers || []).map((follow) => follow.username));

interface ComposeBoxProps {
  onClose: () => void;
  topic: string;
  citationId?: PublicDocId | null;
  placeholder?: string;
  content?: string;
  prefill?: ComposeBoxPrefillIntent;
  onContentChange?: (content: string) => void;
  inheritedVisibility?: DocVisibility;
  className?: string;
  show_hints?: boolean;
  show_controls?: boolean;
  postType?: ProductPostType;
  minRows?: number;
}

/** Describes a one-shot draft initialization request applied once per key. */
export interface ComposeBoxPrefillIntent {
  /** Reapplying requires a new key so parents can issue distinct intents. */
  key: string;
  /** Provides the full prefilled draft text. */
  content: string;
  /** Sets the initial caret or selection anchor. */
  selectionStart: number;
  /** Sets the selection end, defaulting to a collapsed caret. */
  selectionEnd?: number;
  /** Opens the mention picker immediately after prefill when appropriate. */
  openMentionPicker?: boolean;
}

/** Lets parent components (e.g. OmniSearch) trigger submission or focus programmatically. */
export interface ComposeBoxHandle {
  submit: () => Promise<void>;
  focus: () => void;
}

export const ComposeBox = forwardRef<ComposeBoxHandle, ComposeBoxProps>((props, ref) => {
  ComposeBox.displayName = 'ComposeBox';
  const composeLabelId = 'compose-label';
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  /** Records the last one-shot prefill intent already applied to local draft state. */
  const appliedPrefillKeyRef = useRef<string | null>(null);
  /**
   * Keeps a prefill key eligible for one follow-up suggestion recompute after async user-info changes.
   *
   * Invariant: this is set only while an active prefill still owns the mention picker and is cleared as
   * soon as the user edits, inserts a mention, opens invite flow, or dismisses the picker.
   */
  const pendingPrefillSuggestionSyncKeyRef = useRef<string | null>(null);
  /**
   * Remembers that the user navigated suggestion rows for the active prefill key.
   *
   * Invariant: this never means "preserve everything"; it only preserves the current selection index
   * across late suggestion-list refreshes for that same prefill key while pending prefill sync remains
   * active.
   */
  const userNavigatedPrefillSelectionKeyRef = useRef<string | null>(null);
  const [content, setContent] = useState<string>(props.content || '');
  const hasContent = content.trim().length > 0;
  const [isPosting, setIsPosting] = useState<boolean>(false);
  const [showUserSuggestions, setShowUserSuggestions] = useState<boolean>(false);
  const [userSuggestions, setUserSuggestions] = useState<string[]>([]);
  const [cursorPosition, setCursorPosition] = useState<number>(0);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] =
    useState<SuggestionSelectionIndex>(0);
  const [visibility, setVisibility] = useState<DocVisibility>(
    props.inheritedVisibility || DOC_VISIBILITY.Public
  );
  const visibilityError = useVisibilityError(content, visibility, props.inheritedVisibility);
  const canPost = hasContent && !isPosting && !visibilityError;
  const [showVisibilityDropdown, setShowVisibilityDropdown] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [isFeedbackPost, setIsFeedbackPost] = useState(false);
  const [invalidMentions, setInvalidMentions] = useState<string[]>([]);
  const [submitShortcutLabel, setSubmitShortcutLabel] = useState('Ctrl+Enter or Cmd+Enter');
  const [authExpiredError, setAuthExpiredError] = useState(false);
  const [postError, setPostError] = useState<string | null>(null);
  const pathname = usePathname();
  const router = useRouter();
  const { userInfo } = useUserInfo();
  const followedUsersSignature = getFollowedUsersSignature(userInfo?.followed_users);

  useEffect(() => {
    if (props.content !== undefined) {
      setContent(props.content);
    }
  }, [props.content]);

  /** Reports whether no suggestion row is currently selected. */
  const isNoSuggestionSelection = (
    selectionIndex: SuggestionSelectionIndex
  ): selectionIndex is null => selectionIndex === null;

  /** Reports whether the invite row is currently selected. */
  const isInviteSuggestionSelection = (
    selectionIndex: SuggestionSelectionIndex
  ): selectionIndex is typeof INVITE_SUGGESTION_INDEX => selectionIndex === INVITE_SUGGESTION_INDEX;

  /** Reports whether a concrete user-suggestion row is currently selected. */
  const isUserSuggestionSelection = (
    selectionIndex: SuggestionSelectionIndex
  ): selectionIndex is number => selectionIndex !== null && selectionIndex >= 0;

  /** Chooses the initial active row for a bare @ query. */
  const deriveEmptyAtSelectionIndex = (args: {
    suggestions: string[];
    mayInvite: boolean;
  }): SuggestionSelectionIndex => {
    if (args.mayInvite) {
      return INVITE_SUGGESTION_INDEX;
    }

    return args.suggestions.length > 0 ? 0 : null;
  };

  /** Maps a suggestion selection to its DOM row id. */
  const getSuggestionRowId = (index: SuggestionSelectionIndex): string | null => {
    if (isNoSuggestionSelection(index)) {
      return null;
    }

    return isInviteSuggestionSelection(index)
      ? 'user-suggestion-invite'
      : `user-suggestion-${index}`;
  };

  /** Keeps the active suggestion row visible while navigating the picker. */
  const scrollSuggestionIntoView = (index: number | null) => {
    const rowId = getSuggestionRowId(index);
    if (!rowId) {
      return;
    }

    const element = document.getElementById(rowId);
    element?.scrollIntoView({ block: 'nearest' });
  };

  /** Computes the next picker selection for keyboard navigation. */
  const moveSuggestionSelection = (args: {
    previousIndex: SuggestionSelectionIndex;
    direction: 'up' | 'down';
    mayInvite: boolean;
    suggestionCount: number;
  }): SuggestionSelectionIndex => {
    const { previousIndex, direction, mayInvite, suggestionCount } = args;

    if (direction === 'down') {
      if (isNoSuggestionSelection(previousIndex)) {
        if (suggestionCount > 0) {
          return 0;
        }

        return mayInvite ? INVITE_SUGGESTION_INDEX : null;
      }

      if (isInviteSuggestionSelection(previousIndex)) {
        return suggestionCount > 0 ? 0 : INVITE_SUGGESTION_INDEX;
      }

      return Math.min(previousIndex + 1, suggestionCount - 1);
    }

    if (isNoSuggestionSelection(previousIndex)) {
      if (mayInvite) {
        return INVITE_SUGGESTION_INDEX;
      }

      return suggestionCount > 0 ? 0 : null;
    }

    if (isInviteSuggestionSelection(previousIndex)) {
      return INVITE_SUGGESTION_INDEX;
    }

    if (previousIndex === 0 && mayInvite) {
      return INVITE_SUGGESTION_INDEX;
    }

    return Math.max(previousIndex - 1, 0);
  };

  /** Preserves a user-chosen selection when suggestion data refreshes. */
  const deriveSelectionIndex = (args: {
    currentSelection: SuggestionSelectionIndex;
    suggestions: string[];
    mayInvite: boolean;
    query: string;
    preserveSelection: boolean;
  }): SuggestionSelectionIndex => {
    if (args.preserveSelection) {
      if (isInviteSuggestionSelection(args.currentSelection) && args.mayInvite) {
        return INVITE_SUGGESTION_INDEX;
      }

      if (
        isUserSuggestionSelection(args.currentSelection) &&
        args.currentSelection < args.suggestions.length
      ) {
        // TODO(3): Preserving by numeric index can select a different username if `followed_users`
        // reorder between refreshes. Future cleanup should preserve by username identity instead.
        return args.currentSelection;
      }

      if (isNoSuggestionSelection(args.currentSelection)) {
        return null;
      }
    }

    if (args.query.length === 0) {
      return deriveEmptyAtSelectionIndex({
        suggestions: args.suggestions,
        mayInvite: args.mayInvite,
      });
    }

    if (args.suggestions.length > 0) {
      return 0;
    }

    return args.mayInvite ? INVITE_SUGGESTION_INDEX : null;
  };

  /** Stops future late-prefill resync from overwriting active user actions. */
  const clearPendingPrefillTracking = () => {
    pendingPrefillSuggestionSyncKeyRef.current = null;
    userNavigatedPrefillSelectionKeyRef.current = null;
  };

  /**
   * Recomputes feedback, invalid mentions, and @suggestions for a draft snapshot.
   *
   * TODO(2): Extract the prefill synchronization and mention-picker draft/suggestion derivation into a
   * dedicated hook or state machine so this component no longer interleaves one-shot prefill ownership,
   * async user-info refresh handling, and keyboard-selection preservation inside one render scope.
   */
  const syncDraftState = (
    nextContent: string,
    selectionStart: number,
    options?: {
      openMentionPicker?: boolean;
      preserveSelection?: boolean;
    }
  ) => {
    const openMentionPicker = options?.openMentionPicker ?? true;
    const preserveSelection = options?.preserveSelection ?? false;
    setContent(nextContent);
    setCursorPosition(selectionStart);

    const containsFeedback = /@feedback\b/i.test(nextContent);
    setIsFeedbackPost(containsFeedback);

    const completedMentionRegex = /@(\w+)(?=\s|[.!?,:;])/g;
    const completedMentions = Array.from(nextContent.matchAll(completedMentionRegex), (m) => m[1]);
    const endMentionMatch = nextContent.match(/@(\w+)$/);
    if (endMentionMatch && selectionStart <= nextContent.length - endMentionMatch[0].length) {
      completedMentions.push(endMentionMatch[1]);
    }

    const validUsernames = userInfo?.followed_users?.map((follow) => follow.username) || [];
    const validMentions = new Set([...validUsernames, FEEDBACK_USERNAME]);
    const invalid = completedMentions.filter((mention) => !validMentions.has(mention));
    setInvalidMentions(invalid);

    if (!openMentionPicker) {
      setUserSuggestions([]);
      setSelectedSuggestionIndex(null);
      setShowUserSuggestions(false);
      return;
    }

    const lastAtSymbolIndex = nextContent.lastIndexOf('@', selectionStart);
    if (lastAtSymbolIndex === -1 || selectionStart <= lastAtSymbolIndex) {
      setUserSuggestions([]);
      setSelectedSuggestionIndex(null);
      setShowUserSuggestions(false);
      return;
    }

    const query = nextContent.slice(lastAtSymbolIndex + 1, selectionStart);
    if (query.includes(' ')) {
      setUserSuggestions([]);
      setSelectedSuggestionIndex(null);
      setShowUserSuggestions(false);
      return;
    }

    const queryLower = query.toLowerCase();
    const followedUserSuggestions =
      userInfo?.followed_users
        ?.filter((follow: DisplayUserFollow) =>
          follow.username.toLowerCase().startsWith(queryLower)
        )
        .map((follow: DisplayUserFollow) => follow.username) || [];
    const nextSuggestions: string[] = [];
    if (FEEDBACK_USERNAME.startsWith(queryLower)) {
      nextSuggestions.push(FEEDBACK_USERNAME);
    }

    nextSuggestions.push(...Array.from(new Set(followedUserSuggestions)));
    const showInviteOption = query.length === 0 && Boolean(userInfo?.may_invite);

    if (nextSuggestions.length > 0 || showInviteOption) {
      setUserSuggestions(nextSuggestions);
      setShowUserSuggestions(true);

      setSelectedSuggestionIndex(
        deriveSelectionIndex({
          currentSelection: selectedSuggestionIndex,
          suggestions: nextSuggestions,
          mayInvite: showInviteOption,
          query,
          preserveSelection,
        })
      );
    } else {
      setUserSuggestions([]);
      setSelectedSuggestionIndex(null);
      setShowUserSuggestions(false);
    }
  };

  useEffect(() => {
    if (!props.prefill) {
      return;
    }

    const {
      content: nextContent,
      selectionStart,
      selectionEnd = selectionStart,
      openMentionPicker = false,
    } = props.prefill;

    const isNewPrefillKey = appliedPrefillKeyRef.current !== props.prefill.key;

    if (isNewPrefillKey) {
      /**
       * A new prefill key is a one-shot ownership transfer of the draft.
       *
       * Invariant: only a newly observed key may overwrite content/caret from props.prefill. Later
       * reruns for the same key may refresh suggestions from async user info, but must not reset user
       * edits or keyboard navigation state.
       */
      appliedPrefillKeyRef.current = props.prefill.key;
      pendingPrefillSuggestionSyncKeyRef.current = openMentionPicker ? props.prefill.key : null;
      userNavigatedPrefillSelectionKeyRef.current = null;

      syncDraftState(nextContent, selectionStart, {
        openMentionPicker,
      });

      setTimeout(() => {
        const textarea = textareaRef.current;
        if (!textarea) {
          return;
        }

        textarea.focus();
        textarea.setSelectionRange(selectionStart, selectionEnd);
      }, 0);
      return;
    }

    /**
     * Same-key reruns are limited to late suggestion refreshes for the still-owned prefill.
     *
     * Invariant: if the picker is closed or pending prefill sync was cleared by user action, this effect
     * must become a no-op so followed-user updates cannot reopen or retarget suggestions.
     */
    if (!openMentionPicker || pendingPrefillSuggestionSyncKeyRef.current !== props.prefill.key) {
      return;
    }

    const textarea = textareaRef.current;
    const nextSelectionStart =
      textarea && document.activeElement === textarea
        ? (textarea.selectionStart ?? props.prefill.selectionStart)
        : props.prefill.selectionStart;
    syncDraftState(nextContent, nextSelectionStart, {
      openMentionPicker: true,
      preserveSelection: userNavigatedPrefillSelectionKeyRef.current === props.prefill.key,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- this effect is intentionally keyed to prefill.key, not every props.prefill or syncDraftState change
  }, [props.prefill?.key, followedUsersSignature, userInfo?.may_invite]);

  useEffect(() => {
    setSubmitShortcutLabel(getSubmitShortcutLabel());
  }, []);

  /** Drives the live preview toggle — only shown when the draft has markdown syntax. */
  const containsMarkdown = (text: string): boolean => {
    const markdownRegex =
      /(\*\*|__|\*|_|`|#|\[.*\]\(.*\)|!\[.*\]\(.*\)|\n\s*[-*+]\s|\n\s*\d+\.\s|\n\s*>\s)/;
    return markdownRegex.test(text);
  };

  useImperativeHandle(ref, () => ({
    submit: handlePost,
    focus: () => textareaRef.current?.focus(),
  }));

  /**
   * Submits the compose box content as a new document.
   * On 401, shows login-and-return guidance while preserving the user's draft.
   * Other failures surface a generic inline error so posting problems are never silent.
   */
  const handlePost = async () => {
    if (!canPost) {
      return;
    }

    setAuthExpiredError(false);
    setPostError(null);
    setIsPosting(true);
    try {
      const topic = props.topic;
      const requestBody: NewDocRequest = {
        content,
        topic,
        visibility,
      };

      if (props.postType) {
        requestBody.post_type = props.postType;
      }

      if (props.citationId) {
        requestBody.citation = props.citationId;
      }

      console.log('Posting content:', requestBody);

      const response = await fetch(`${API_URL}/doc/new`, {
        credentials: 'include',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (response.status === 401) {
        setAuthExpiredError(true);
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to post content (${response.status})`);
      }

      const responseData = await response.json();
      console.log('New doc id:', responseData.doc_id);

      props.onClose();
      router.push(`/doc/${asPublicDocId(responseData.doc_id)}?processing_started=1`);
    } catch (error) {
      console.error('Error posting content:', error);
      setPostError("We couldn't post your message. Your draft is still here. Please try again.");
    } finally {
      setIsPosting(false);
    }
  };

  const loginHref = `/login?redirect=${encodeURIComponent(pathname || '/')}`;

  const showPreview = containsMarkdown(content);

  /** Also tracks @mention state and suggestion visibility alongside draft sync. */
  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const newContent = e.target.value;
    const selectionStart = e.target.selectionStart || 0;
    clearPendingPrefillTracking();
    syncDraftState(newContent, selectionStart, {
      openMentionPicker: true,
    });

    if (props.onContentChange) {
      props.onContentChange(newContent);
    }
  };

  /**
   * Multiplexes keyboard input: arrow/enter/tab/escape navigate the @mention
   * dropdown when it's open, otherwise Ctrl+Enter (Cmd+Enter on Mac) submits.
   */
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (showUserSuggestions) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (props.prefill && appliedPrefillKeyRef.current === props.prefill.key) {
          userNavigatedPrefillSelectionKeyRef.current = props.prefill.key;
        }
        setSelectedSuggestionIndex((prevIndex) => {
          const newIndex = moveSuggestionSelection({
            previousIndex: prevIndex,
            direction: 'down',
            mayInvite: Boolean(userInfo?.may_invite),
            suggestionCount: userSuggestions.length,
          });
          scrollSuggestionIntoView(newIndex);
          return newIndex;
        });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (props.prefill && appliedPrefillKeyRef.current === props.prefill.key) {
          userNavigatedPrefillSelectionKeyRef.current = props.prefill.key;
        }
        setSelectedSuggestionIndex((prevIndex) => {
          const newIndex = moveSuggestionSelection({
            previousIndex: prevIndex,
            direction: 'up',
            mayInvite: Boolean(userInfo?.may_invite),
            suggestionCount: userSuggestions.length,
          });
          scrollSuggestionIntoView(newIndex);
          return newIndex;
        });
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        if (isNoSuggestionSelection(selectedSuggestionIndex)) {
          setShowUserSuggestions(false);
          return;
        }

        e.preventDefault();
        if (isInviteSuggestionSelection(selectedSuggestionIndex) && userInfo?.may_invite) {
          clearPendingPrefillTracking();
          setShowInviteModal(true);
          setShowUserSuggestions(false);
        } else if (
          isUserSuggestionSelection(selectedSuggestionIndex) &&
          selectedSuggestionIndex < userSuggestions.length
        ) {
          insertUsername(userSuggestions[selectedSuggestionIndex]);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        clearPendingPrefillTracking();
        setShowUserSuggestions(false);
      }
    } else if (e.key === 'Enter' && (isApplePlatform() ? e.metaKey : e.ctrlKey)) {
      e.preventDefault();
      if (!canPost) {
        return;
      }

      handlePost();
    }
  };

  /** Splices the selected username into the draft at the active @-query position. */
  const insertUsername = (username: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const cursor = textarea.selectionStart ?? cursorPosition;
    const lastAtSymbolIndex = content.lastIndexOf('@', cursor);
    if (lastAtSymbolIndex === -1) return;

    const beforeAt = content.slice(0, lastAtSymbolIndex);
    const afterCursor = content.slice(cursor);
    const needsTrailingSpace = afterCursor.length === 0 || !/^\s/.test(afterCursor);
    const insertion = `@${username}${needsTrailingSpace ? ' ' : ''}`;
    const newContent = `${beforeAt}${insertion}${afterCursor}`;
    clearPendingPrefillTracking();
    setContent(newContent);
    setCursorPosition(lastAtSymbolIndex + insertion.length);
    setShowUserSuggestions(false);

    setTimeout(() => {
      const newCursorPosition = lastAtSymbolIndex + insertion.length;
      textarea.setSelectionRange(newCursorPosition, newCursorPosition);
      textarea.focus();
    }, 0);
  };

  // Auto-focus the text area when component mounts
  useEffect(() => {
    // Small delay to ensure the component is fully rendered
    const timer = setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      {/* Close button when show_controls is true */}
      {props.show_controls && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <IconButton onClick={props.onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      )}

      {/* Feedback notice */}
      {isFeedbackPost && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Feedback posts are <strong>always visible</strong> to the General Wisdom team, but
            won&apos;t appear in your public profile unless you set visibility to Public.
          </Typography>
        </Alert>
      )}

      {/* Invalid mentions warning */}
      {invalidMentions.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Warning: You mentioned @{invalidMentions.join(', @')} but you don&apos;t follow{' '}
            {invalidMentions.length > 1 ? 'these users' : 'this user'}. Only people you follow will
            be notified.
          </Typography>
        </Alert>
      )}

      {postError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2">{postError}</Typography>
        </Alert>
      )}

      {authExpiredError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button
              component="a"
              href={loginHref}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
            >
              Log in
            </Button>
          }
        >
          <Typography variant="body2">
            Your session expired. Your draft is still here — log in, then return to this tab and
            click Post again.
          </Typography>
        </Alert>
      )}

      {/* Main compose area */}
      <Card elevation={1} sx={{ overflow: 'visible' }}>
        <CardContent sx={{ position: 'relative', overflow: 'visible' }}>
          {props.show_controls !== false && (
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
            >
              <Typography variant="h6" component="h2">
                {props.citationId ? 'Reply' : 'New Post'}
              </Typography>
              <IconButton onClick={props.onClose} size="small" aria-label="Close compose box">
                <CloseIcon />
              </IconButton>
            </Box>
          )}
          <Box sx={{ position: 'relative' }}>
            <TextField
              inputRef={textareaRef}
              multiline
              fullWidth
              minRows={props.minRows || 3}
              maxRows={20}
              placeholder={props.placeholder || 'What are you thinking?'}
              value={content}
              onChange={handleTextareaChange}
              onKeyDown={handleKeyDown}
              disabled={isPosting}
              aria-labelledby={composeLabelId}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '1rem',
                  lineHeight: 1.5,
                },
              }}
            />

            {/* User suggestions dropdown */}
            {showUserSuggestions && (
              <Paper
                elevation={8}
                sx={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  mt: 1,
                  maxHeight: 200,
                  overflow: 'auto',
                  zIndex: 1400, // Higher than dialogs (1300) and other UI elements
                  backgroundColor: 'background.paper', // Explicit background
                  opacity: 1, // Full opacity
                }}
              >
                <List dense>
                  {userInfo?.may_invite && (
                    <ListItemButton
                      id="user-suggestion-invite"
                      selected={isInviteSuggestionSelection(selectedSuggestionIndex)}
                      onClick={() => {
                        clearPendingPrefillTracking();
                        setShowInviteModal(true);
                        setShowUserSuggestions(false);
                      }}
                    >
                      <ListItemIcon>
                        <PersonAddIcon />
                      </ListItemIcon>
                      <ListItemText primary="Invite someone new..." />
                    </ListItemButton>
                  )}
                  {userSuggestions.map((username, index) => (
                    <ListItemButton
                      key={`${username}-${index}`}
                      id={`user-suggestion-${index}`}
                      selected={selectedSuggestionIndex === index}
                      onClick={() => insertUsername(username)}
                    >
                      <ListItemIcon>
                        {username === FEEDBACK_USERNAME ? <CommentIcon /> : null}
                      </ListItemIcon>
                      <ListItemText primary={`@${username}`} />
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            )}
          </Box>

          {/* Markdown preview */}
          {showPreview && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Preview:
                </Typography>
                <Box sx={{ mt: 1, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                  {/* Using ReactMarkdown for live preview - instant feedback for users while typing.
                      Final document rendering uses backend-parsed structured content for consistency. */}
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
                </Box>
              </Box>
            </>
          )}

          {/* Help text when typing */}
          {content.length > 0 && props.show_controls !== false && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Markdown is supported. Press {submitShortcutLabel} to post. Enter inserts a new line.
            </Typography>
          )}

          {/* Controls */}
          {props.show_controls !== false && (
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {visibilityError && (
                  <Alert severity="error" sx={{ py: 0, px: 1 }}>
                    <Typography variant="caption">{visibilityError}</Typography>
                  </Alert>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <VisibilitySelector
                  visibility={visibility}
                  setVisibility={setVisibility}
                  inheritedVisibility={props.inheritedVisibility}
                  showDropdown={showVisibilityDropdown}
                  setShowDropdown={setShowVisibilityDropdown}
                  position="above"
                />
                <Button
                  variant="contained"
                  onClick={handlePost}
                  disabled={!canPost}
                  title={`Post (${submitShortcutLabel})`}
                >
                  {isPosting ? 'Posting...' : 'Post'}
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Hints */}
      {props.show_hints && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            <strong>Markdown supported:</strong> **bold**, *italic*, `code`, [links](url), etc.
          </Typography>
          <br />
          <Typography variant="caption" color="text.secondary">
            <strong>@mentions:</strong> Type @ to mention followed users or invite new ones
          </Typography>
        </Box>
      )}

      {/* Invite modal */}
      <Modal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        title="Invite someone to General Wisdom"
      >
        <InviteForm
          onInviteCreated={(inviteCode, inviteUrl) => {
            // TODO(2): ENG-965 — Keep this modal open on invite success so the user can copy/share the
            // generated invite code/URL here; today it closes immediately, discarding them and preventing
            // any ComposeBox-local QR or copy flow.
            setShowInviteModal(false);
            // Since we don't get the invited user's username back,
            // we can't automatically insert it into the compose box
          }}
          onCancel={() => setShowInviteModal(false)}
        />
      </Modal>
    </Box>
  );
});

ComposeBox.displayName = 'ComposeBox';
